import { easingLinear } from 'pf-frontend-common/src/helper/easing/linear';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { WindowServiceInterface } from 'pf-frontend-common/src/service/window/service.interface';

import { chooseAOrDefault } from 'common/helper/choose/a-or-default';
import { DataStore } from 'common/module/data/store';
import { DataStoreEvent } from 'common/module/data/store.event';
import { GalleryViewStore } from 'common/module/gallery/view-store';
import { ImageGalleryItemTypeEnum } from 'common/module/image-gallery/item-type.enum';

import { ImageGalleryTemplatePropsInterface } from './template-props.interface';
import { GalleryViewStoreOptionsInterface } from './view-store-options.interface';

export class ImageGalleryViewStore extends DataStore {
  /**
   * @inheritDoc
   */
  protected state: ImageGalleryTemplatePropsInterface;

  /**
   * Constructor
   */
  constructor(
    eventEmitter: EventEmitterInterface,
    private windowService: WindowServiceInterface,
    protected galleryStore: GalleryViewStore
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public getState(): ImageGalleryTemplatePropsInterface {
    return this.state;
  }

  /**
   * @inheritDoc
   */
  public initState(): void {
    this.state = {
      popupItems: [],
      gallery: this.galleryStore.getState(),
      items: [],
      isShowingAll: false,
      feature: {
        showAll: false,
        thumbnails: false,
        counter: false,
        webp: true,
      },
      onClickButtonViewAll: this.onClickButtonViewAll,
      onClickButtonCloseViewAll: this.onClickButtonCloseViewAll,
      onClickThumbnail: this.onClickThumbnail,
    };
  }

  /**
   * @inheritDoc
   */
  public initialize(options: GalleryViewStoreOptionsInterface): void {
    this.galleryStore.initialize({
      itemsCount: options.items.length,
      isRtl: options.isRtl,
      currentIndex: options.currentIndex,
      singleImagePreview: !!options?.feature?.singleImagePreview,
      onClick: options.onGalleryClick || null,
      onIndexChange: options.onIndexChange || null,
    });

    this.initState();

    this.state.items = options.items;

    if (options.onGalleryScroll) {
      this.state.onScroll = options.onGalleryScroll;
    }

    // Optional feature
    if (options.feature) {
      this.state.feature.showAll = chooseAOrDefault(options.feature.showAll, this.state.feature.showAll);
      this.state.feature.thumbnails = chooseAOrDefault(options.feature.thumbnails, this.state.feature.thumbnails);
      this.state.feature.counter = chooseAOrDefault(options.feature.counter, this.state.feature.counter);
      this.state.feature.webp = chooseAOrDefault(options.feature.webp, this.state.feature.webp);
    }

    this.galleryStore.getEventEmitter().addListener(DataStoreEvent.updateState, this.onUpdateStateGalleryViewStore);
  }

  /**
   * Get original gallery view store
   */
  public getGallery(): GalleryViewStore {
    return this.galleryStore;
  }

  /**
   * Show all the items at once or as a slider
   */
  public setShowAll(isShowingAll: boolean): void {
    this.setState(
      {
        ...this.getState(),
        isShowingAll,
      },
      this.setShowAll
    );
  }

  /**
   * Returns the current item's type
   */
  public getCurrentItemType(): ImageGalleryItemTypeEnum {
    const state = this.getState();

    return state.items[state.gallery.currentIndex].type;
  }

  /**
   * @inheritDoc
   */
  public destroy(): void {
    this.galleryStore.getEventEmitter().removeListener(DataStoreEvent.updateState, this.onUpdateStateGalleryViewStore);

    this.galleryStore.destroy();
  }

  /**
   * Clicked 'view all' Button
   */
  private onClickButtonViewAll = (): void => {
    this.setShowAll(true);
  };

  /**
   * Clicked 'close view all' button
   */
  private onClickButtonCloseViewAll = (): void => {
    this.setShowAll(false);

    // scroll to top
    this.windowService.scrollToY(0, 0, easingLinear);
  };

  /**
   * Clicked a thumbnail
   */
  private onClickThumbnail = (currentIndex: number): void => {
    this.galleryStore.setCurrentIndex(currentIndex);
  };

  /**
   * Gallery state updated
   */
  private onUpdateStateGalleryViewStore = (): void => {
    this.state.gallery = this.galleryStore.getState();
    this.setState(this.state);
  };
}
